import React from 'react'
import type { ReactNode } from 'react'
import { flexRender, type RowData, type Table } from '@tanstack/react-table'
import { Pressable, ScrollView, Text, View } from 'react-native'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'left' | 'center'
    skeletonCell?: ReactNode
  }
}

interface AdminDataTableProps<T> {
  table: Table<T>
  minWidth?: number
  alignRows?: 'center' | 'top'
  onRowPress?: (row: T) => void
  rowAccessibilityLabel?: (row: T) => string
  children?: React.ReactNode
}

export function AdminDataTable<T>({
  table,
  minWidth = 500,
  alignRows = 'center',
  onRowPress,
  rowAccessibilityLabel,
  children,
}: AdminDataTableProps<T>) {
  const rows = table.getRowModel().rows

  return (
    <ScrollView
      horizontal
      scrollEnabled={false}
      className="mt-5"
      contentContainerStyle={{ minWidth, flexGrow: 1, maxWidth: '100%' }}
    >
      <View className="flex-1">
        <View className="min-h-[32px] flex-row items-center border-b border-border pb-1">
          {table.getHeaderGroups()[0]?.headers.map((header, hi, arr) => {
            const align = header.column.columnDef.meta?.align ?? 'left'
            return (
              <View key={header.id} style={{ flex: header.getSize() }} className={hi < arr.length - 1 ? 'pr-3' : ''}>
                {header.isPlaceholder ? null : (
                  <Text
                    numberOfLines={1}
                    className={`text-xs font-bold uppercase tracking-[0.8px] text-primary ${
                      align === 'center' ? 'text-center' : ''
                    }`}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext()) as string}
                  </Text>
                )}
              </View>
            )
          })}
        </View>

        {children !== undefined ? children : rows.map((row, index) => {
          const isLast = index === rows.length - 1
          const rowContent = (
            <View
              className={`min-h-[56px] flex-row ${alignRows === 'top' ? 'items-start' : 'items-center'} ${
                !isLast ? 'border-b border-border' : ''
              }`}
            >
              {row.getVisibleCells().map((cell, ci, arr) => (
                <View
                  key={cell.id}
                  style={{ flex: cell.column.getSize() }}
                  className={`py-2 ${ci < arr.length - 1 ? 'pr-3' : ''}`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </View>
              ))}
            </View>
          )

          return onRowPress ? (
            <Pressable
              key={row.id}
              onPress={() => onRowPress(row.original)}
              accessibilityRole="button"
              accessibilityLabel={rowAccessibilityLabel?.(row.original)}
            >
              {rowContent}
            </Pressable>
          ) : (
            <View key={row.id}>{rowContent}</View>
          )
        })}
      </View>
    </ScrollView>
  )
}